import React, { useState } from 'react';
import { X, Star } from 'lucide-react';
import { TopicMultiSelect } from '@/components/shared/TopicMultiSelect';

interface QuizSettingsModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (settings: QuizSettings) => void;
  availableTopics?: string[];
}

export interface QuizSettings {
  questionTypes: string[];
  difficulty: string;
  questionCount: number;
  topics: string[];
  instructions: string;
}

const QUESTION_TYPES = [
  { id: 'multiple', label: 'Multiple Choice' },
  { id: 'free', label: 'Free Response' },
  { id: 'trueFalse', label: 'True or False' },
  { id: 'fillBlank', label: 'Fill in the blank' }
];

const DIFFICULTIES = [
  { id: 'Easy', label: 'Easy', stars: 1 },
  { id: 'Medium', label: 'Medium', stars: 2 },
  { id: 'Hard', label: 'Hard', stars: 3 }
];

export const QuizSettingsModal: React.FC<QuizSettingsModalProps> = ({
  open,
  onClose,
  onSubmit,
  availableTopics = [],
}) => {
  const [selectedQuestionTypes, setSelectedQuestionTypes] = useState<string[]>(['Multiple Choice']);
  const [selectedDifficulty, setSelectedDifficulty] = useState('Medium');
  const [questionCount, setQuestionCount] = useState('');
  const [instructions, setInstructions] = useState('');
  const [selectedTopics, setSelectedTopics] = useState<string[]>([]);

  if (!open) return null;

  const toggleQuestionType = (type: string) => {
    if (selectedQuestionTypes.includes(type)) {
      setSelectedQuestionTypes(selectedQuestionTypes.filter(t => t !== type));
    } else {
      setSelectedQuestionTypes([...selectedQuestionTypes, type]);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      questionTypes: selectedQuestionTypes,
      difficulty: selectedDifficulty,
      questionCount: parseInt(questionCount) || 10,
      topics: selectedTopics.length > 0 ? selectedTopics : ['All Topics'],
      instructions
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-x-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm animate-in fade-in-0"
        onClick={onClose}
      />
      
      {/* Dialog Content */}
      <section 
        role="dialog"
        aria-labelledby="quiz-dialog-title"
        aria-describedby="quiz-dialog-description"
        className="relative z-50 mx-1 my-1 sm:mx-6 sm:my-16 flex flex-col w-full max-w-xl rounded-xl border border-border bg-background py-1 shadow-lg"
      >
        {/* Close Button */}
        <button
          type="button"
          onClick={onClose}
          className="absolute right-2 top-2 z-10 rounded-full p-1 text-muted-foreground hover:text-foreground"
        >
          <X className="h-5 w-5" />
        </button>

        {/* Header */}
        <header className="px-6 py-4 pb-0 mb-0 flex flex-col space-y-1.5 text-center sm:text-left" id="quiz-dialog-title">
          <div className="text-lg font-normal text-foreground">Customize Quiz</div>
          <div className="text-sm font-normal text-muted-foreground">
            Create quiz sets with preferred question types, difficulty, and more.
          </div>
        </header>

        {/* Form */}
        <div className="flex flex-1 flex-col gap-3 px-6 py-2 pt-0 mt-4 text-sm" id="quiz-dialog-description">
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Question Types */}
            <div className="space-y-2">
              <label className="text-sm font-medium leading-none text-foreground">
                Question Types <span className="text-destructive">*</span>
              </label>
              <div className="w-full rounded-2xl border border-[hsl(var(--selected))]/20 bg-background p-1.5">
                <div className="flex flex-wrap gap-1.5">
                  {QUESTION_TYPES.map((type) => (
                    <button
                      key={type.id}
                      type="button"
                      onClick={() => toggleQuestionType(type.label)}
                      className={`flex items-center justify-center gap-1.5 whitespace-nowrap rounded-xl border px-2 py-1.5 text-sm transition-all duration-200 ${
                        selectedQuestionTypes.includes(type.label)
                          ? 'bg-[hsl(var(--selected))]/10 text-[hsl(var(--selected))] border-[hsl(var(--selected))]/20'
                          : 'bg-background text-muted-foreground border-border hover:border-muted-foreground hover:text-foreground'
                      }`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Difficulty */}
            <div className="space-y-2">
              <label className="text-sm font-medium leading-none text-foreground">
                Difficulty <span className="text-destructive">*</span>
              </label>
              <div className="w-full rounded-2xl border border-[hsl(var(--color-yellow))]/20 bg-background p-1.5">
                <div className="flex flex-wrap gap-1.5">
                  {DIFFICULTIES.map((diff) => (
                    <button
                      key={diff.id}
                      type="button"
                      onClick={() => setSelectedDifficulty(diff.id)}
                      className={`flex items-center gap-1.5 rounded-xl border px-2 py-1.5 text-sm transition-all duration-200 ${
                        selectedDifficulty === diff.id
                          ? 'bg-[hsl(var(--color-yellow))]/10 text-[hsl(var(--color-yellow))] border-[hsl(var(--color-yellow))]/20'
                          : 'bg-background text-muted-foreground border-border hover:border-muted-foreground hover:text-foreground'
                      }`}
                    >
                      <span className="flex items-center gap-[2px]">
                        {[...Array(diff.stars)].map((_, i) => (
                          <Star key={i} className="h-3.5 w-3.5" />
                        ))}
                      </span>
                      {diff.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Number of Questions */}
            <div className="space-y-2">
              <label htmlFor="question-count" className="text-sm font-medium leading-none text-foreground">
                Number of Questions
              </label>
              <input
                id="question-count"
                type="number"
                min="1"
                max="30"
                placeholder="10"
                value={questionCount}
                onChange={(e) => setQuestionCount(e.target.value)}
                className="flex h-10 w-full rounded-lg border border-input bg-background px-4 text-sm transition-all duration-200 placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring text-foreground"
              />
            </div>

            {/* Topics */}
            <div className="space-y-2">
              <label className="text-sm font-medium leading-none text-foreground">
                Topics
                {selectedTopics.length > 0 && (
                  <span className="ml-2 text-xs text-muted-foreground">
                    ({selectedTopics.length} selected)
                  </span>
                )}
              </label>
              <TopicMultiSelect
                availableTopics={availableTopics}
                selectedTopics={selectedTopics}
                onChange={setSelectedTopics}
                placeholder="Optional: Select concepts to focus on"
              />
            </div>

            {/* Instructions */}
            <div className="space-y-2">
              <label htmlFor="quiz-instructions" className="text-sm font-medium leading-none text-foreground">
                What should the quiz focus on?
              </label>
              <textarea
                id="quiz-instructions"
                placeholder="Focus on the parts that are about..."
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                className="flex min-h-[80px] max-h-[80px] w-full rounded-xl border border-input bg-background text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring p-3 resize-none text-foreground"
              />
            </div>
          </form>
        </div>

        {/* Footer */}
        <footer className="flex flex-row gap-2 px-6 py-4 justify-end">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center whitespace-nowrap rounded-lg text-sm font-medium transition-colors bg-secondary text-secondary-foreground hover:bg-accent h-10 px-4 py-2"
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={selectedQuestionTypes.length === 0}
            className="inline-flex items-center justify-center whitespace-nowrap rounded-lg text-sm font-medium transition-colors bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Generate
          </button>
        </footer>
      </section>
    </div>
  );
};
